import { roundTo } from './stats.ts'

export const STALL_BLOCKING_MS = 1000
export const STALL_HIDDEN_MS = 2500
export const STALL_WINDOWS = 2
export const MAX_STALLS = 10

export const UI_PANELS = ['bento', 'people', 'settings'] as const
export type UiPanel = (typeof UI_PANELS)[number]

export const RECORDER_STATES = ['off', 'recording', 'uploading'] as const
export type RecorderState = (typeof RECORDER_STATES)[number]

export const INVOKER_TYPES = [
  'user-callback',
  'event-listener',
  'resolve-promise',
  'reject-promise',
  'classic-script',
  'module-script',
] as const
export type InvokerType = (typeof INVOKER_TYPES)[number]

export const CONNECTION_TYPES = [
  'bluetooth',
  'cellular',
  'ethernet',
  'wifi',
  'wimax',
  'none',
  'other',
  'unknown',
] as const
export type ConnectionType = (typeof CONNECTION_TYPES)[number]

export const EFFECTIVE_TYPES = ['slow-2g', '2g', '3g', '4g'] as const
export type EffectiveType = (typeof EFFECTIVE_TYPES)[number]

export type SourceBucket = 'inline' | 'extension' | 'other' | `${string}.js`

export function allowedOrNull<T extends string>(
  list: readonly T[],
  value: string | null | undefined,
): T | null {
  return list.find((item) => item === value) ?? null
}

export interface PageUiSnapshot {
  ui_panel: UiPanel | null
  ui_floating: boolean | null
  ui_fullscreen: boolean | null
  recorder_state: RecorderState | null
  remote_video_tracks: number | null
}

export interface PageHealthWindow extends PageUiSnapshot {
  loaf_count: number
  loaf_blocking_ms: number
  loaf_max_ms: number
  loaf_layout_ms: number
  loaf_script_ms: number
  loaf_top_invoker_type: InvokerType | null
  loaf_top_source: SourceBucket | null
  loaf_top_function: string | null
  hidden_ms: number
  focused: boolean
  resize_loops: number
  resize_events: number
  battery_charging: boolean | null
  battery_level: number | null
  connection_type: ConnectionType | null
  connection_effective: EffectiveType | null
  duration_ms: number
}

export interface PageSnapshot {
  page_blocking_ms: number
  page_layout_ms: number
  page_script_ms: number
  page_top_invoker_type: InvokerType | null
  page_top_source: SourceBucket | null
  page_hidden_ms: number
  page_focused: boolean
  page_resize_loops: number
  page_battery_charging: boolean | null
  page_ui_panel: UiPanel | null
  page_recorder_state: RecorderState | null
}

export function pageSnapshotOf(window: PageHealthWindow): PageSnapshot {
  return {
    page_blocking_ms: window.loaf_blocking_ms,
    page_layout_ms: window.loaf_layout_ms,
    page_script_ms: window.loaf_script_ms,
    page_top_invoker_type: window.loaf_top_invoker_type,
    page_top_source: window.loaf_top_source,
    page_hidden_ms: window.hidden_ms,
    page_focused: window.focused,
    page_resize_loops: window.resize_loops,
    page_battery_charging: window.battery_charging,
    page_ui_panel: window.ui_panel,
    page_recorder_state: window.recorder_state,
  }
}

export interface PageStallReport extends Omit<PageHealthWindow, 'duration_ms'> {
  duration_s: number
}

export interface PageHealthTotals {
  stalls: number
  loaf_count: number
  loaf_blocking_ms: number
  hidden_s: number
  resize_loops: number
  focused_share: number | null
}

export interface StallDetector {
  push(window: PageHealthWindow): PageStallReport | null
  flush(): PageStallReport | null
  totals(): PageHealthTotals
}

export function judgePageWindow(window: PageHealthWindow): boolean {
  return window.loaf_blocking_ms > STALL_BLOCKING_MS || window.hidden_ms > STALL_HIDDEN_MS
}

interface OpenStall {
  windows: number
  durationMs: number
  report: PageStallReport
}

export function createStallDetector(): StallDetector {
  let open: OpenStall | null = null
  let reported = 0
  let stalls = 0
  let closedWindows = 0
  let focusedWindows = 0
  let loafCount = 0
  let blockingMs = 0
  let hiddenMs = 0
  let resizeLoops = 0

  function begin(window: PageHealthWindow): OpenStall {
    const { duration_ms: durationMs, ...rest } = window
    return { windows: 1, durationMs, report: { ...rest, duration_s: 0 } }
  }

  function absorb(stall: OpenStall, window: PageHealthWindow) {
    const { report } = stall
    stall.windows += 1
    stall.durationMs += window.duration_ms
    report.loaf_count += window.loaf_count
    report.loaf_blocking_ms += window.loaf_blocking_ms
    report.loaf_layout_ms += window.loaf_layout_ms
    report.loaf_script_ms += window.loaf_script_ms
    report.loaf_max_ms = Math.max(report.loaf_max_ms, window.loaf_max_ms)
    report.hidden_ms += window.hidden_ms
    report.resize_loops += window.resize_loops
    report.resize_events += window.resize_events
  }

  function close(): PageStallReport | null {
    const stall = open
    open = null
    if (!stall || stall.windows < STALL_WINDOWS) return null
    stalls += 1
    if (reported >= MAX_STALLS) return null
    reported += 1
    return { ...stall.report, duration_s: roundTo(stall.durationMs / 1000, 1) ?? 0 }
  }

  return {
    push(window) {
      closedWindows += 1
      if (window.focused) focusedWindows += 1
      loafCount += window.loaf_count
      blockingMs += window.loaf_blocking_ms
      hiddenMs += window.hidden_ms
      resizeLoops += window.resize_loops

      if (judgePageWindow(window)) {
        if (open) absorb(open, window)
        else open = begin(window)
        return null
      }
      return close()
    },
    flush() {
      return close()
    },
    totals() {
      return {
        stalls,
        loaf_count: loafCount,
        loaf_blocking_ms: blockingMs,
        hidden_s: roundTo(hiddenMs / 1000, 1) ?? 0,
        resize_loops: resizeLoops,
        focused_share: closedWindows === 0 ? null : roundTo(focusedWindows / closedWindows, 2),
      }
    },
  }
}
